<script>
  import { modifyImageUrl } from '$lib/formatters';
  import Cashback from '$components/+cashback.svelte';
  import { browser } from '$app/environment';
  import { page } from '$app/stores';
  import { updateUrlParams } from '$lib/updateUrlParams';
  import { afterNavigate, goto } from '$app/navigation';
  import { onMount } from 'svelte';

  /**
   * The location data.
   * @type {object}
   * @property {string} uuid - Unique identifier of the location.
   * @property {string} name - The name of the location.
   * @property {object} rewards - Rewards associated with the location.
   * @property {object} type_object - Type information for the location.
   * @property {object} category - Category information for the location.
   * @property {object} images - Images associated with the location.
   */
  export let item;

  /**
   * The link destination for the location.
   * @type {string}
   */
  export let href;

  /**
   * Indicates if the location is promoted.
   * @type {boolean}
   */
  export let promoted = false;

  /**
   * Determines if the current path is `/find-partnere`.
   * @type {boolean}
   */
  let isOnFindPartnersPage = false;

  // Reactive statement to check the current path
  $: if (browser) {
    isOnFindPartnersPage = $page.url.pathname === '/find-partnere';
  }

  /**
   * Handles the card click event.
   * Opens a modal or updates the URL based on the current page.
   *
   * @param {string} uuid - The UUID of the location.
   */
  function handleCardClick(uuid) {
    if (browser) {
      if (isOnFindPartnersPage) {
        // Dispatch event to open modal
        const event = new CustomEvent('open-modal', { detail: { uuid } });
        window.dispatchEvent(event);
      } else {
        // Update the URL for other pages
        updateUrlParams({ 'location-uuid': uuid });
      }
    } else {
      // Fallback for server navigation
      goto('/find-partners/' + uuid, { replaceState: true, noScroll: true, keepFocus: false });
    }
  }

  /** @type {HTMLImageElement} */
  let imageObj;

  /** @type {Boolean} */
  $: imageLoaded = false;
  $: imageObj;

  /**
   * Loads the hero image.
   * @returns {Promise<void>}
   */
  async function imageLoad() {
    if (browser && isOnFindPartnersPage) {
      var img = new Image();

      img.onload = function () {
        imageLoaded = true;
        imageObj = img;
      };

      img.src = modifyImageUrl(item.images?.hero) ?? '';
    }
  }

  /**
   * Reload images upon URL changes upon filter changes.
   */
  afterNavigate(() => {
    if (isOnFindPartnersPage) {
      imageLoaded = false;
      imageLoad();
    }
  });

  /**
   * Initializes the component by pre-loading the hero image.
   */
  onMount(() => {
    if (isOnFindPartnersPage) {
      imageLoad();
    } else {
      imageLoaded = true;
    }
  });
</script>

{#if !imageLoaded}
  <div class="location-card placeholder">
    <div class="card-hero">
      <div class="card-logo"></div>
    </div>
    <div class="content">
      <div class="line"></div>
      <div class="line"></div>
    </div>
  </div>
{:else}
  <div class="location-card">
    <a {href} on:click|preventDefault={() => handleCardClick(item.uuid)} data-sveltekit-noscroll data-sveltekit-preload-data>
      {#if item.rewards?.cashback_text}
        <Cashback cashback_text={item.rewards?.cashback_text} />
      {:else}
        <Cashback cashback={item.rewards?.first_visit.rate} />
      {/if}
      <div class="card-hero">
        <picture>
          <img src={imageObj ? imageObj.src : modifyImageUrl(item.images?.hero)} alt={item?.name} />
        </picture>
        <div class="card-logo">
          <img src={modifyImageUrl(item.images?.logo, { width: 200 })} alt={item?.name} />
        </div>
        {#if promoted}
          <div class="card-logo promoted">
            <span>Sponsoreret</span>
          </div>
        {/if}
      </div>

      <div class="content">
        <h3>{item.name}</h3>
        <!--TODO update to use tags component-->
        <div class="tags">
          <span class="type">{item.type_object?.name}</span>
          <span class="category">{item.category?.name}</span>
        </div>
      </div>
    </a>
  </div>
{/if}
